import { Configuration, UserAccessApi, PermissionsApi, } from '../../generated/clients/index.js';
import { permissionTreeSchema } from './auth-service.validator.js';
import { parseAuthServiceError } from '../errors.js';
import { ResponseError, } from '../../generated/clients/runtime.js';
export class CookieAuthServiceAuthorizationProvider {
    name = 'auth-service';
    brandId;
    user;
    permissions;
    userAccessApi;
    permissionsApi;
    /**
     * Creates a cookie-based Auth Service authorization provider.
     * Instead of passing an access token, this provider relies on
     * HTTP cookies (credentials: 'include') to authenticate requests
     * to the auth service. An X-CSRFToken header is attached per-request
     * via a pre-fetch middleware that reads the value from the
     * `getCsrfToken` callback supplied by the caller.
     */
    constructor(config) {
        this.brandId = config.brandId;
        this.user = null;
        this.permissions = null;
        const csrfMiddleware = {
            pre: async ({ url, init }) => {
                const csrfToken = config.getCsrfToken();
                if (!csrfToken)
                    return { url, init };
                const headers = new Headers(init.headers);
                headers.set('X-CSRFToken', csrfToken);
                return { url, init: { ...init, headers } };
            },
        };
        const configuration = new Configuration({
            basePath: config.authServiceUrl,
            credentials: 'include',
            middleware: [csrfMiddleware],
        });
        this.userAccessApi = new UserAccessApi(configuration);
        this.permissionsApi = new PermissionsApi(configuration);
    }
    /**
     * Initialize the provider by fetching user information via cookies
     */
    async initialize() {
        try {
            const [userResponse, permissionsResponse] = await Promise.all([
                this.userAccessApi.getMyAccess(this.brandId),
                this.permissionsApi.getMyPermissions(this.brandId),
            ]);
            this.permissions = permissionsResponse;
            this.user = {
                brandId: this.brandId,
                locationIds: userResponse.locationIds,
                id: userResponse.id,
            };
        }
        catch (error) {
            if (error instanceof ResponseError) {
                throw await parseAuthServiceError(error.response);
            }
            throw error;
        }
    }
    /**
     * Get the permission tree from the Auth Service
     */
    async getPermissionTree() {
        const { brandId } = this.getUserContext();
        if (!brandId) {
            throw new Error('Brand ID is missing');
        }
        try {
            let myPermissions;
            if (this.permissions) {
                myPermissions = this.permissions;
            }
            else {
                myPermissions = await this.permissionsApi.getMyPermissions(brandId);
            }
            const permissionTree = permissionTreeSchema.parse(myPermissions);
            return permissionTree;
        }
        catch (error) {
            if (error instanceof ResponseError) {
                throw await parseAuthServiceError(error.response);
            }
            throw error;
        }
    }
    /**
     * Get user context from the Auth Service
     */
    getUserContext() {
        if (!this.user) {
            throw new Error('Package must be initialized');
        }
        const brandRoles = Array.from(new Set(Object.values(this.permissions?.rolesByLocation ?? {}).flat()));
        return {
            userId: this.user.id,
            email: null,
            phone: null,
            brandId: this.brandId,
            brandRoles,
            locationIds: this.user.locationIds,
        };
    }
    /**
     * Get the auth provider type
     */
    getAuthProvider() {
        return 'auth-service';
    }
}
//# sourceMappingURL=cookie-auth-service.provider.js.map